import { createApi } from '@reduxjs/toolkit/query/react';
import { API, API_PREFIX } from '../../constants';
import { baseQuery } from './base.query';
import {
  ActivityListRequestParameters,
  SearchProfileRequestParameters,
  WritingAssistantRequest,
  CreateActivityModel,
  LandingActivity,
  Category,
  Topic,
  TopicChild,
  TopicSubTopic,
  CategoryRequestParameters,
  CreateInteractionModel,
  ProfileResult,
  NameValue,
  TeamParameters,
  DeleteActivityRequestParameters,
  ProfileCaptureDetails,
  ProfileCaptureDetailsParameters,
  InsightSourceOption,
  InsightSourceRequestParameters,
} from '../../models/insights-capture.model';

export const transformTopicsToGroup = (data: Topic[]) => {
  return data.reduce((acc: Array<TopicSubTopic>, topic: Topic): any[] => {
    return [
      ...acc,
      ...topic.children.map((child: TopicChild) => ({
        groupName: topic.name,
        groupId: topic.id,
        name: child.name,
        id: child.id,
      })),
    ];
  }, []);
};

export const buildProfileCaptureDetailsQuery = (args: ProfileCaptureDetailsParameters) => ({
  url: `${API_PREFIX.NEXTGEN}${API.PROFILE_CAPTURE_DETAILS}?externalProfileId=${args.profileId}`,
  // Propagate the browser attempt ID into the API logger so App Insights
  // stages and Railway backend events can be queried as one timeline.
  headers: {
    'X-Capture-Deeplink-Trace-Id': args.captureDeeplinkTraceId,
  },
});

export const insightsCaptureApi = createApi({
  reducerPath: 'insightsCaptureApi',
  baseQuery,
  endpoints: (builder) => ({
    getProfileCaptureDetails: builder.query<ProfileCaptureDetails, ProfileCaptureDetailsParameters>(
      {
        query: buildProfileCaptureDetailsQuery,
      }
    ),
    getActivityList: builder.query<LandingActivity[], ActivityListRequestParameters>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.ACTIVITY}?draftItems=${args.draftItems}&count=${args.count}`,
      }),
    }),
    deleteActivity: builder.mutation<unknown, DeleteActivityRequestParameters>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.ACTIVITY}/${args.id}/${args.isParentActivityId}/${args.teamId}`,
        method: 'DELETE',
      }),
    }),
    createActivity: builder.mutation<unknown, CreateActivityModel>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.ACTIVITY}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...args }),
      }),
    }),
    getActivity: builder.mutation<unknown, number>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.ACTIVITY}/${args}`,
      }),
    }),
    searchProfiles: builder.query<ProfileResult[], SearchProfileRequestParameters>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.PROFILE}?query=${args.query}&filterByTerritory=${args.filterByTerritory}&teamId=${args.teamId}`,
      }),
    }),
    getCategories: builder.query<Category[], CategoryRequestParameters>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.INSIGHTS_DETAIL}/${API.SURVEYS}/?profileIds=${args.profileIds}&teamId=${args.teamId}`,
      }),
    }),
    getTopics: builder.query<TopicSubTopic[], TeamParameters>({
      query: () => ({ url: `${API_PREFIX.NEXTGEN}${API.TOPICS}` }),
      transformResponse(baseQueryReturnValue) {
        const data = baseQueryReturnValue as Topic[];
        return transformTopicsToGroup(data);
      },
    }),
    getFieldTeam: builder.query<NameValue[], TeamParameters>({
      query: () => ({ url: `${API_PREFIX.NEXTGEN}${API.FIELD_TEAM}` }),
    }),
    getWritingAssitant: builder.mutation<any, WritingAssistantRequest>({
      query: (args: WritingAssistantRequest) => ({
        url: `${API_PREFIX.ELSIEAI}${API.WRITING_ASSISTANT}`,
        method: 'POST',
        body: JSON.stringify({ ...args }),
      }),
    }),
    createInteraction: builder.mutation<unknown, CreateInteractionModel>({
      query: (args) => ({
        url: `${API_PREFIX.NEXTGEN}${API.INTERACTION}`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ...args }),
      }),
    }),
    getInsightSources: builder.query<InsightSourceOption[], InsightSourceRequestParameters>({
      query: ({ activityDate, teamId }) => ({
        url: `${API_PREFIX.NEXTGEN}${API.ACTIVITY}/insight-sources`,
        params: { activityDate, teamId },
      }),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetActivityMutation,
  useGetProfileCaptureDetailsQuery,
  useGetActivityListQuery,
  useDeleteActivityMutation,
  useCreateActivityMutation,
  useCreateInteractionMutation,
  useSearchProfilesQuery,
  useGetCategoriesQuery,
  useGetTopicsQuery,
  useGetFieldTeamQuery,
  useGetWritingAssitantMutation,
  useGetInsightSourcesQuery,
} = insightsCaptureApi;
